import React, { useEffect, useState } from "react";
import { Flex, Spinner, useToast } from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import {
  getPatientDetailsAction,
  deletePatientAction,
} from "../../../../actions/patientActions";
import { primary, white } from "../../../../utils/colors";
import ToastComponent from "../../../../components/partials/ToastComponent";
import { useTranslation } from "react-i18next";
import PatientHeader from "./partials/PatientHeader";
import PatientDetails from "./partials/PatientDetails";
import PatientPlansNew from "./partials/PatientPlansNew";
import SidePanel from "../../../../components/sidepanelNew/SidePanel";
import {
  TREATMENTPLAN_DELETE_RESET,
  TREATMENTPLAN_CREATE_RESET,
  TREATMENTPLAN_UPDATE_RESET,
  TREATMENTPLAN_DUPLICATE_RESET,
} from "../../../../constants/treatmentPlanConstants";
import useRoleCheck from "../../../../hooks/useRoleCheck";
import { useParams } from "react-router-dom";
import PatientActivityLog from "./partials/PatientActivityLog";
import CreateTreatmentPlanNew from "../../treatmentplans/sidepanels/CreateTreatmentPlanNew";
import useThemeColors from "../../../../hooks/useThemeColors";
import EditPatientNew from "../sidepanels/EditPatientNew";
import { UPDATE_PATIENT_RESET } from "../../../../constants/patientConstants";
import useScrollToTop from "../../../../hooks/useScrollToTop";
import ConfirmDeleteModal from "../../../../components/modals/ConfirmDeleteModal";
import PatientPortal from "./partials/PatientPortal";
// import SendTreatmentPlanModal from "../../../../components/modals/SendTreatmentPlanModal";
import { Helmet } from "react-helmet";
import PatientDetailsMobile from "./partials/PatientDetailsMobile";
import useIsMobile from "../../../../hooks/useIsMobile";
import DrawerNew from "../../../../components/menu/DrawerNew";
import useCurrencySymbol from "../../../../hooks/useCurrencySymbol";
import UserNoAccess from "../../../../components/partials/UserNoAccess";

const PatientSingleNew = ({ close }) => {
  useScrollToTop();

  // GENERAL
  const { slug } = useParams();
  const patientId = useParams().id;
  const dispatch = useDispatch();
  const toastComponent = ToastComponent();
  const { t } = useTranslation();
  const toast = useToast();
  const isMobile = useIsMobile();

  // COLORS
  const { whiteMode, backgroundLightMode } = useThemeColors();

  // ROLES
  // const isDoctor = useRoleCheck("employee_doctor");
  // const isAdmin = useRoleCheck("admin");

  // STATE
  const [loadingDisplay, setLoadingDisplay] = useState(true);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [patientName, setPatientName] = useState("");
  const [isModalSending, setIsModalSending] = useState(false);
  const [fetchUnavailable, setFetchUnavailable] = useState(true);

  const [patientPortalOn, setPatientPortalOn] = useState(false);
  const [sendEmailModalOpen, setSendEmailModalOpen] = useState(false);

  //  SIDEPANELS
  const [
    createPatientTreatmentPlanSidePanel,
    setCreatePatientTreatmentPlanSidePanel,
  ] = useState(false);

  const [
    editPatientTreatmentPlanSidePanel,
    setEditPatientTreatmentPlanSidePanel,
  ] = useState(false);

  // REDUX
  const authLogin = useSelector((state) => state.authLogin);
  const { userInfo } = authLogin;

  const getPatientDetails = useSelector((state) => state.getPatientDetails);
  const { loading, error, patient } = getPatientDetails;

  const tenantDetailsBySlug = useSelector((state) => state.tenantDetailsBySlug);
  const { tenant } = tenantDetailsBySlug;

  const updatePatient = useSelector((state) => state.updatePatient);
  const {
    error: errorUpdatePatient,
    success: successUpdatePatient,
    loading: loadingUpdatePatient,
  } = updatePatient;

  const treatmentPlanUpdate = useSelector((state) => state.treatmentPlanUpdate);
  const {
    success: successTreatmentPlanUpdate,
    error: errorTreatmentPlanUpdate,
    loading: loadingTreatmentPlanUpdate,
  } = treatmentPlanUpdate;

  const treatmentPlanCreate = useSelector((state) => state.treatmentPlanCreate);
  const {
    success: successTreatmentPlanCreate,
    error: errorTreatmentPlanCreate,
    loading: loadingTreatmentPlanCreate,
  } = treatmentPlanCreate;

  const treatmentPlanDelete = useSelector((state) => state.treatmentPlanDelete);
  const { success: successTreatmentPlanDelete } = treatmentPlanDelete;

  const treatmentPlanDuplicate = useSelector(
    (state) => state.treatmentPlanDuplicate
  );
  const {
    success: successTreatmentPlanDuplicate,
    error: errorTreatmentPlanDuplicate,
    loading: loadingTreatmentPlanDuplicate,
  } = treatmentPlanDuplicate;

  const treatmentPlanSendEmail = useSelector(
    (state) => state.treatmentPlanSendEmail
  );
  const {
    loading: loadingSendEmail,
    error: errorSendEmail,
    success: successSendEmail,
  } = treatmentPlanSendEmail;

  // HANDLERS
  const deletePatientHandler = () => {
    dispatch(deletePatientAction(patientId, slug));
  };

  const openCreatePatientTreatmentPlanSidePanel = () => {
    setCreatePatientTreatmentPlanSidePanel(true);

    document.body.style.overflow = "hidden";
  };

  const closeCreatePatientTreatmentPlanSidePanel = () => {
    setCreatePatientTreatmentPlanSidePanel(false);

    document.body.style.overflow = "auto";
  };

  const openEditPatientTreatmentPlanSidePanel = () => {
    setEditPatientTreatmentPlanSidePanel(true);

    document.body.style.overflow = "hidden";
  };

  const closeEditPatientTreatmentPlanSidePanel = () => {
    setEditPatientTreatmentPlanSidePanel(false);

    document.body.style.overflow = "auto";
  };

  // FETCH DATA
  const fetchUserDetails = () => {
    dispatch(getPatientDetailsAction(patientId, slug));
  };

  // USEEFFECT
  useEffect(() => {
    const timeout = setTimeout(() => {
      setLoadingDisplay(false);
    }, 500); // CHANGE THIS TO HAVE A LONGER LOADING TIME

    // Cleanup function to clear the timeout if the component unmounts
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (!patient || patient._id !== patientId) {
      fetchUserDetails();
    } else {
    }
  }, [dispatch, patientId, patient]);

  useEffect(() => {
    if (successTreatmentPlanDelete) {
      toastComponent.showToast(
        t("toasts.treatmentPlanDelete"),
        "success",
        3000
      );
      dispatch({ type: TREATMENTPLAN_DELETE_RESET });
    } else if (successTreatmentPlanUpdate) {
      toastComponent.showToast(
        t("toasts.treatmentPlanUpdate"),
        "success",
        3000
      );
      dispatch({ type: TREATMENTPLAN_UPDATE_RESET });
    } else if (successTreatmentPlanCreate) {
      toastComponent.showToast(
        t("toasts.treatmentPlanCreate"),
        "success",
        3000
      );
      closeCreatePatientTreatmentPlanSidePanel();

      dispatch({ type: TREATMENTPLAN_CREATE_RESET });
    } else if (successUpdatePatient) {
      toastComponent.showToast(t("toasts.patientUpdate"), "success", 3000);

      closeEditPatientTreatmentPlanSidePanel();

      dispatch({ type: UPDATE_PATIENT_RESET });
    } else if (errorUpdatePatient) {
      if (errorUpdatePatient === "errorEmailAlreadyExists") {
        toastComponent.showToast(
          t("toasts.patientCreateErrorEmailExists"),
          "error",
          3000
        );
      } else {
        toastComponent.showToast(t("toasts.patientUpdateError"), "error", 3000);
      }
    }

    fetchUserDetails();
  }, [
    successTreatmentPlanDelete,
    successTreatmentPlanCreate,
    successTreatmentPlanUpdate,
    successUpdatePatient,
    errorUpdatePatient,
    successTreatmentPlanDuplicate,
    errorTreatmentPlanDuplicate,
    dispatch,
  ]);

  useEffect(() => {
    if (successTreatmentPlanDuplicate) {
      toastComponent.showToast(
        t("toasts.treatmentPlanDuplicate"),
        "success",
        3000
      );
    } else if (errorTreatmentPlanDuplicate) {
      console.log("errorTreatmentPlanDuplicate", errorTreatmentPlanDuplicate);
      if (
        errorTreatmentPlanDuplicate ===
        "Monthly limit reached. Cannot duplicate treatment plan."
      ) {
        toast({
          title: t("toasts.treatmentPlanLimitReached"),
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      } else {
        toastComponent.showToast(
          t("toasts.treatmentPlanDuplicateError"),
          "error",
          3000
        );
      }
    }

    dispatch({ type: TREATMENTPLAN_DUPLICATE_RESET });
  }, [successTreatmentPlanDuplicate, errorTreatmentPlanDuplicate]);

  useEffect(() => {
    if (errorTreatmentPlanCreate) {
      if (
        errorTreatmentPlanCreate ===
        "Monthly limit reached. Cannot create more treatment plans."
      ) {
        toast({
          title: t("toasts.treatmentPlanLimitReached"),
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      } else {
        toast({
          title: t("toasts.treatmentPlanCreateError"),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    }

    dispatch({ type: TREATMENTPLAN_CREATE_RESET });
  }, [errorTreatmentPlanCreate]);

  const currencySymbol = useCurrencySymbol(tenant && tenant.currency);

  // CHECK PERMISSIONS

  const currentTenantSlug = slug || tenant?.slug;

  // 1. Get permissions for current tenant
  const currentTenantAccess = userInfo?.tenants?.find(
    (t) => t.slug === currentTenantSlug
  );

  const hasAccess = currentTenantAccess?.permissions?.patients?.on;
  // const hasAccessToCreate = currentTenantAccess?.permissions?.patients?.create;
  const hasAccessToDelete = currentTenantAccess?.permissions?.patients?.delete;
  const hasAccessToUpdate = currentTenantAccess?.permissions?.patients?.update;
  const hasAccessToSensitiveData =
    currentTenantAccess?.permissions?.patients?.sensitiveData;

  const hasTreatmentPlansAccess =
    currentTenantAccess?.permissions?.treatmentPlans?.on;

  const hasTreatmentPlansCreateAccess =
    currentTenantAccess?.permissions?.treatmentPlans?.create;

  const hasTreatmentPlansEditAccess =
    currentTenantAccess?.permissions?.treatmentPlans?.update;

  const hasTreatmentPlansDeleteAccess =
    currentTenantAccess?.permissions?.treatmentPlans?.delete;

  const hasTreatmentPlansDuplicateAccess =
    currentTenantAccess?.permissions?.treatmentPlans?.duplicate;

  const hasTreatmentPlansSendToPatientAccess =
    currentTenantAccess?.permissions?.treatmentPlans?.sendToPatient;

  useEffect(() => {
    // Early exit if permissions are not loaded yet
    if (!currentTenantAccess?.permissions) return;

    const perms = currentTenantAccess.permissions;

    if (perms.patients?.on) return; // ✅ allowed, stay on dashboard
  }, [currentTenantAccess, slug]);

  if (hasAccess === false)
    return (
      <>
        <UserNoAccess pageName={t("noAccess.patientSingle")} />
      </>
    );

  // console.log("hasAccess", hasAccess);

  // LOGS
  // console.log("patient", patient);
  // console.log("patientId", patientId);

  // console.log("isSending", isSending);
  // console.log("successSendEmail", successSendEmail);

  // console.log("isModalSending", isModalSending);
  // console.log("fetchUnavailable", fetchUnavailable);

  // console.log("setSendEmailModalOpen", sendEmailModalOpen);

  // console.log("sendToPatientAccess", hasTreatmentPlansSendToPatientAccess);
  // console.log("hasAccessToUpdate", hasAccessToUpdate);
  // console.log("hasAccessToSensitiveData", hasAccessToSensitiveData);
  // console.log("hasAccessToDelete", hasAccessToDelete);
  // console.log("hasTreatmentPlansAccess", hasTreatmentPlansAccess);

  return (
    <>
      {/* HELMET  */}
      <Helmet>
        <title>
          {patient && patient.patientID
            ? `${t("pageTitle.patientSingle")} - ${patient.patientID}`
            : t("pageTitle.patientSingle")}
        </title>
        <meta name="description" content={t("pageDescription.patientSingle")} />
      </Helmet>

      {/* SIDEPANELS  START */}
      {!isMobile ? (
        <SidePanel
          open={createPatientTreatmentPlanSidePanel}
          close={closeCreatePatientTreatmentPlanSidePanel}
          title={t("treatmentPlan.createTreatmentPlan")}
          noBorder={true}
        >
          <Flex
            w="100%"
            bg={white}
            minW="800px"
            maxW="800px"
            position="relative"
            zIndex="900"
          >
            {hasTreatmentPlansCreateAccess ? (
              <CreateTreatmentPlanNew
                tenant={tenant}
                close={closeCreatePatientTreatmentPlanSidePanel}
                currentPatient={patient}
                currentUser={userInfo}
                isPatientPage
                currency={tenant && tenant.currency}
              />
            ) : (
              <UserNoAccess
                pageName={t("noAccess.treatmentPlanCreate")}
                p="0"
              />
            )}
          </Flex>
        </SidePanel>
      ) : (
        <DrawerNew
          isOpen={createPatientTreatmentPlanSidePanel}
          onClose={closeCreatePatientTreatmentPlanSidePanel}
          title={t("treatmentPlan.createTreatmentPlan")}
          size="full"
        >
          {hasTreatmentPlansCreateAccess ? (
            <CreateTreatmentPlanNew
              tenant={tenant}
              close={closeCreatePatientTreatmentPlanSidePanel}
              currentPatient={patient}
              currentUser={userInfo}
              isPatientPage
              currency={tenant && tenant.currency}
            />
          ) : (
            <UserNoAccess pageName={t("noAccess.treatmentPlanCreate")} p="0" />
          )}
        </DrawerNew>
      )}

      {!isMobile ? (
        <SidePanel
          open={editPatientTreatmentPlanSidePanel}
          close={closeEditPatientTreatmentPlanSidePanel}
          title={t("patient.editTreatmentPlan")}
          noBorder={true}
        >
          <Flex w="100%" bg={white} minW="800px" maxW="800px">
            {hasAccessToUpdate ? (
              <EditPatientNew
                patient={patient}
                close={closeEditPatientTreatmentPlanSidePanel}
              />
            ) : (
              <UserNoAccess pageName={t("noAccess.patientEdit")} p="0" />
            )}
          </Flex>
        </SidePanel>
      ) : (
        <DrawerNew
          isOpen={editPatientTreatmentPlanSidePanel}
          onClose={closeEditPatientTreatmentPlanSidePanel}
          title={t("patient.editTreatmentPlan")}
          size="full"
        >
          {hasAccessToUpdate ? (
            <EditPatientNew
              patient={patient}
              close={closeEditPatientTreatmentPlanSidePanel}
            />
          ) : (
            <UserNoAccess pageName={t("noAccess.patientEdit")} p="0" />
          )}
        </DrawerNew>
      )}
      {/* SIDEPANELS  END */}

      {/* MODALS START */}
      {/* {sendEmailModalOpen && (
        <SendTreatmentPlanModal
          planId={null}
          planID={null}
          open={sendEmailModalOpen}
          setOpen={setSendEmailModalOpen}
          patient={patient}
          isModalSending={isModalSending}
          setIsModalSending={setIsModalSending}
          fetchUserDetails={fetchUserDetails}
          fetchUnavailable={fetchUnavailable}
          setFetchUnavailable={setFetchUnavailable}
          hasAccess={hasTreatmentPlansSendToPatientAccess}
        />
      )} */}

      {showDeleteConfirmation && (
        <ConfirmDeleteModal
          hasAccessToDelete={hasAccessToDelete}
          noAccessPageName={t("noAccess.patientDelete")}
          deleteTitle={t("patient.deletePatientTitle")}
          deleteName={patientName}
          warningLabel={t("patient.deleteWarning")}
          isOpen={showDeleteConfirmation}
          onClose={() => setShowDeleteConfirmation(false)}
          confirmAction={() => {
            deletePatientHandler(patientId); // Use `userId` set from `MoreButtonLite`
            setShowDeleteConfirmation(false);
          }}
          hasAccessSendToPatient={hasTreatmentPlansSendToPatientAccess}
        />
      )}
      {/* MODALS END */}

      {loadingDisplay || loading ? (
        <Flex
          direction="column"
          w="100%"
          h="100%"
          p={["calc(80px + 2rem) 2rem", "calc(80px + 2rem) 2rem"]}
          // bg="green"
        >
          <Flex
            w="100%"
            align="center"
            justify="center"
            bg={whiteMode}
            borderRadius="2rem"
            minH="90vh"
            p="3rem 0"
          >
            <Spinner size="xl" color={primary} />
          </Flex>
        </Flex>
      ) : (
        <>
          {patient && patient.treatmentPlans && (
            <Flex
              direction="column"
              w="100%"
              // h="100%"

              p={["0px 0 0rem", "calc(80px + 2rem) 2rem 3rem 2rem"]}
              position={["relative", "relative"]}
              zIndex={[9999999, "initial"]}
              overflow={["hidden", "initial"]}
            >
              <Flex
                w="100%"
                direction="column"
                bg={whiteMode}
                borderRadius={["0rem", "2rem"]}
                p={["0 0 6rem", "0"]}
              >
                <PatientHeader
                  slug={slug}
                  patient={patient}
                  setShowDeleteConfirmation={setShowDeleteConfirmation}
                  setPatientName={setPatientName}
                  openEdit={openEditPatientTreatmentPlanSidePanel}
                />

                <Flex
                  w="100%"
                  justify="space-between"
                  p={["0 0rem", "0 1.5rem", "0px 2rem 4rem 2rem"]}
                  direction={["column", "row", "row"]}
                  bg={[backgroundLightMode, "initial"]}
                >
                  <Flex
                    w={["100%", "100%", "calc(100% - 350px)"]}
                    direction="column"
                    p={["2rem 0rem 0 0rem", "2rem 2rem 0 0rem"]}
                  >
                    {isMobile ? (
                      <PatientDetailsMobile
                        user={patient}
                        openEdit={openEditPatientTreatmentPlanSidePanel}
                      />
                    ) : (
                      <PatientDetails
                        slug={slug}
                        tenant={tenant}
                        userInfo={userInfo}
                        user={patient}
                        openEdit={openEditPatientTreatmentPlanSidePanel}
                      />
                    )}

                    <PatientPlansNew
                      slug={slug}
                      patientId={patientId}
                      buttonHandler={openCreatePatientTreatmentPlanSidePanel}
                      userInfo={userInfo}
                      currency={tenant && tenant.currency}
                      hasTreatmentPlansAccess={hasTreatmentPlansAccess}
                      hasTreatmentPlansCreateAccess={
                        hasTreatmentPlansCreateAccess
                      }
                      hasTreatmentPlansEditAccess={hasTreatmentPlansEditAccess}
                      hasTreatmentPlansDeleteAccess={
                        hasTreatmentPlansDeleteAccess
                      }
                      hasTreatmentPlansDuplicateAccess={
                        hasTreatmentPlansDuplicateAccess
                      }
                      hasTreatmentPlansSendToPatientAccess={
                        hasTreatmentPlansSendToPatientAccess
                      }
                    />
                  </Flex>

                  <Flex
                    w={["100%", "350px"]}
                    direction="column"
                    p={["0rem 0rem 0 0rem", "2rem 0rem 0 0rem"]}
                  >
                    <PatientPortal
                      slug={slug}
                      patient={patient}
                      tenant={tenant}
                      on={patientPortalOn}
                      setOn={setPatientPortalOn}
                      setSendEmailModalOpen={setSendEmailModalOpen}
                    />
                    <PatientActivityLog
                      slug={slug}
                      id={patientId}
                      patient={patient}
                    />
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          )}
        </>
      )}
    </>
  );
};

export default PatientSingleNew;
